import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { DateTime } from 'luxon';
import { GET_HOST_INVOICES_BY_REFUND_STATUS } from '../../services/invoiceServices/queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { RefundStatus } from '../../../__generated__/graphql';
import InvoicesListSkeleton from '../skeletons/InvoicesListSkeleton';

interface BookingState {
  startAt: number;
  endAt: number;
  totalPrice: number;
  carPricePerTrip?: number;
  insurancePerTrip?: number;
  pickupLocationId: number;
  returnLocationId: number;
}

interface Invoice {
  id: string;
  shortId?: string;
  amount: number;
  createdAt: string;
  hostRefundReviewedAt?: string | null;
  refundStatus: string;
  type: string;
  note?: string | null;
  booking: {
    id: string;
    startAt: number;
    endAt: number;
    cancellationReason?: string | null;
  } | null;
  originalBookingState?: BookingState | null;
  newBookingState?: BookingState | null;
  requester: {
    id: string;
    displayName: string;
    email?: string;
  } | null;
  host: {
    id: string;
    displayName: string;
    email?: string;
  } | null;
}

interface InvoicesResponse {
  invoicesByRefundStatusForHost: {
    items: Invoice[];
    total: number;
  };
}

const Container = styled.div`
  ${tw`mx-auto w-full max-w-full min-w-0 max-w-6xl p-4 md:p-6 box-border`}
`;

const InvoicesGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-6 max-w-full min-w-0 box-border`}
  @media (min-width: 1024px) {
    ${tw`hidden`}
  }
`;

const TableWrapper = styled.div`
  ${tw`hidden mt-6 bg-white border border-border-default rounded-card shadow-card overflow-hidden`}
  @media (min-width: 1024px) {
    ${tw`block`}
  }
`;

const Table = styled.table`
  ${tw`w-full text-sm`}
`;

const TableHead = styled.thead`
  ${tw`bg-bg-secondary text-text-secondary`}
`;

const TableRow = styled.tr`
  ${tw`border-b border-border-default cursor-pointer hover:bg-bg-secondary`}
`;

const TableHeader = styled.th`
  ${tw`text-left px-4 py-3 text-[11px] uppercase font-bold tracking-wider`}
`;

const TableCell = styled.td`
  ${tw`px-4 py-3 text-text-primary align-middle`}
`;

const TableCellMuted = styled(TableCell)`
  ${tw`text-text-secondary`}
`;

const InvoiceCard = styled.div`
  ${tw`bg-white rounded-card border border-border-default shadow-card p-5 transition-all duration-200 cursor-pointer flex flex-col`}
  &:hover {
    ${tw`shadow-card`}
  }
`;

const CardHeader = styled.div`
  ${tw`flex justify-between items-start mb-4`}
`;

const CardBody = styled.div`
  ${tw`space-y-3 flex-1`}
`;

const CardFooter = styled.div`
  ${tw`mt-4 pt-4 border-t border-gray-50 flex justify-between items-center`}
`;

const InfoRow = styled.div`
  ${tw`flex flex-col`}
`;

const InfoLabel = styled.span`
  ${tw`text-[10px] uppercase font-bold text-text-tertiary tracking-wider mb-0.5`}
`;

const InfoValue = styled.span`
  ${tw`text-sm font-semibold text-text-primary truncate`}
`;

const AmountText = styled.div`
  ${tw`text-lg font-extrabold text-[var(--color-black)]`}
`;

const DateText = styled.div`
  ${tw`text-xs text-text-tertiary font-medium`}
`;

const Pagination = styled.div`
  ${tw`flex justify-between items-center mt-8`}
`;

const PageButton = styled.button`
  ${tw`flex items-center space-x-2 px-4 py-2 text-sm font-bold text-text-secondary bg-white border border-border-default rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-card`}
  &:not(:disabled):hover {
    ${tw`bg-bg-secondary`}
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  ${tw`px-3 py-1 text-[11px] font-extrabold rounded-full uppercase tracking-tighter`}
  ${({ status }) => {
    switch (status) {
      case RefundStatus.PendingHostReview:
        return tw`bg-yellow-50 text-yellow-600 border border-yellow-100`;
      case RefundStatus.Processing:
        return tw`bg-bg-secondary text-black border border-border-default`;
      case RefundStatus.Completed:
        return tw`bg-green-50 text-green-600 border border-green-100`;
      case RefundStatus.Failed:
        return tw`bg-red-50 text-red-600 border border-red-100`;
      case RefundStatus.Cancelled:
        return tw`bg-bg-secondary text-text-secondary border border-border-default`;
      case RefundStatus.None:
        return tw`bg-bg-secondary text-text-secondary border border-border-default`;
      default:
        return tw`bg-bg-secondary text-text-secondary border border-border-default`;
    }
  }}
`;

const StatusPicker = styled.div`
  ${tw`w-full flex overflow-x-auto pb-2 space-x-2 md:justify-center`}
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StatusTab = styled.button<{ $active: boolean }>`
  ${tw`flex-shrink-0 px-5 py-2 text-xs font-extrabold rounded-full transition-all duration-200 whitespace-nowrap`}
  ${({ $active }) =>
    $active ? tw`bg-black text-white shadow-card` : tw`bg-bg-secondary text-text-secondary`}
  ${({ $active }) =>
    !$active &&
    css`
      &:hover {
        ${tw`bg-bg-secondary`}
      }
    `}
`;

export function InvoicesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  // Get status from URL params, or default to PendingHostReview
  const statusParam = searchParams.get('status') as RefundStatus | null;
  const [selectedStatus, setSelectedStatus] = useState<RefundStatus>(
    statusParam || RefundStatus.PendingHostReview,
  );
  const pageSize = 9;
  const navigate = useNavigate();

  // Sync selectedStatus with URL params when URL changes (e.g., returning from invoice details)
  useEffect(() => {
    if (statusParam && statusParam !== selectedStatus) {
      setSelectedStatus(statusParam);
      setPage(0); // Reset to first page when status changes from URL
    } else if (!statusParam && selectedStatus !== RefundStatus.PendingHostReview) {
      // If no status param and not default, add it to URL
      setSearchParams({ status: selectedStatus });
    }
  }, [statusParam]); // Only depend on statusParam to avoid infinite loops

  const { data, loading, error } = useQuery<InvoicesResponse>(GET_HOST_INVOICES_BY_REFUND_STATUS, {
    variables: {
      filter: {
        refundStatus: selectedStatus,
        skip: page * pageSize,
        take: pageSize,
        orderBy: 'DESC',
      },
    },
  });

  const handleStatusChange = (status: RefundStatus) => {
    setSelectedStatus(status);
    setPage(0);
    // Update URL params to persist status
    setSearchParams({ status });
  };

  const handleCardClick = (invoiceId: string) => {
    // Pass current status as state to preserve it when returning
    navigate(`/invoice/${invoiceId}`, { state: { returnStatus: selectedStatus } });
  };

  const formatDate = (timestamp: number | undefined) => {
    if (!timestamp) return 'N/A';
    return DateTime.fromMillis(timestamp).toFormat('MM/dd/yyyy');
  };

  const formatShortDate = (isoDate: string | null | undefined) => {
    if (!isoDate) return 'N/A';
    return DateTime.fromISO(isoDate).toFormat('MM/dd/yy');
  };

  const getTripDates = (invoice: Invoice) => {
    if (invoice.booking) {
      return `${DateTime.fromMillis(invoice.booking.startAt).toFormat('MM/dd/yyyy')} - ${DateTime.fromMillis(invoice.booking.endAt).toFormat('MM/dd/yyyy')}`;
    } else if (invoice.originalBookingState) {
      return `${formatDate(invoice.originalBookingState.startAt)} - ${formatDate(invoice.originalBookingState.endAt)}`;
    } else if (invoice.newBookingState) {
      return `${formatDate(invoice.newBookingState.startAt)} - ${formatDate(invoice.newBookingState.endAt)}`;
    }
    return 'N/A';
  };

  if (loading) return <InvoicesListSkeleton />;
  if (error)
    return <div tw="text-red-500 font-bold p-8 text-center italic">Error: {error.message}</div>;
  if (!data) return <div tw="text-text-secondary p-8 text-center italic">No data available</div>;

  const { items, total } = data.invoicesByRefundStatusForHost;
  const totalPages = Math.ceil(total / pageSize);
  const getStatusLabel = (status: string) => status.replace(/_/g, ' ');
  const getEmail = (user: { email?: string; displayName: string } | null) =>
    user ? user.email || user.displayName || 'Unknown' : 'Unknown';

  return (
    <Container>
      <StatusPicker>
        {[
          { key: RefundStatus.PendingHostReview, label: 'Pending Review' },
          { key: RefundStatus.Processing, label: 'Processing' },
          { key: RefundStatus.Completed, label: 'Completed' },
          { key: RefundStatus.Failed, label: 'Failed' },
          { key: RefundStatus.Cancelled, label: 'Cancelled' },
          { key: RefundStatus.None, label: 'All' },
        ].map(status => (
          <StatusTab
            key={status.key}
            $active={selectedStatus === status.key}
            onClick={() => handleStatusChange(status.key)}
          >
            {status.label}
          </StatusTab>
        ))}
      </StatusPicker>

      {items.length === 0 ? (
        <div tw="w-full text-center py-20 text-text-tertiary font-medium italic">
          No refund invoices found for this status.
        </div>
      ) : (
        <>
          <TableWrapper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Invoice ID</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Created At</TableHeader>
                  <TableHeader>Requester (Email)</TableHeader>
                  <TableHeader>Status</TableHeader>
                </TableRow>
              </TableHead>
              <tbody>
                {items.map(invoice => (
                  <TableRow key={invoice.id} onClick={() => handleCardClick(invoice.id)}>
                    <TableCell>
                      {(invoice as any).shortId?.toUpperCase() ||
                        invoice.id.slice(0, 8).toUpperCase()}
                    </TableCell>
                    <TableCellMuted>{invoice.type.replace(/_/g, ' ')}</TableCellMuted>
                    <TableCell>${Math.abs(invoice.amount).toFixed(2)}</TableCell>
                    <TableCellMuted>{formatShortDate(invoice.createdAt)}</TableCellMuted>
                    <TableCellMuted>{getEmail(invoice.requester)}</TableCellMuted>
                    <TableCell>
                      <StatusBadge status={invoice.refundStatus}>
                        {getStatusLabel(invoice.refundStatus)}
                      </StatusBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <InvoicesGrid>
            {items.map(invoice => (
              <InvoiceCard key={invoice.id} onClick={() => handleCardClick(invoice.id)}>
                <CardHeader>
                  <div>
                    <DateText>Issued on</DateText>
                    <InfoValue>
                      {DateTime.fromISO(invoice.createdAt).toFormat('MMM dd, yyyy')}
                    </InfoValue>
                  </div>
                  <StatusBadge status={invoice.refundStatus}>
                    {getStatusLabel(invoice.refundStatus)}
                  </StatusBadge>
                </CardHeader>

                <CardBody>
                  <div tw="grid grid-cols-2 gap-4">
                    <InfoRow>
                      <InfoLabel>Requester</InfoLabel>
                      <InfoValue>{invoice.requester?.displayName || 'Unknown'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Host</InfoLabel>
                      <InfoValue>{invoice.host?.displayName || 'Unknown'}</InfoValue>
                    </InfoRow>
                  </div>

                  <InfoRow>
                    <InfoLabel>Refund Type</InfoLabel>
                    <InfoValue>{invoice.type.replace(/_/g, ' ')}</InfoValue>
                  </InfoRow>

                  <InfoRow>
                    <InfoLabel>Trip Dates</InfoLabel>
                    <InfoValue>{getTripDates(invoice)}</InfoValue>
                  </InfoRow>
                </CardBody>

                <CardFooter>
                  <div tw="flex flex-col">
                    <InfoLabel>Total Refund</InfoLabel>
                    <AmountText>${Math.abs(invoice.amount).toFixed(2)}</AmountText>
                  </div>
                  <div tw="text-[var(--color-black)] text-sm font-bold flex items-center">
                    View Details
                    <FontAwesomeIcon icon={faChevronRight} tw="ml-1.5 text-[10px]" />
                  </div>
                </CardFooter>
              </InvoiceCard>
            ))}
          </InvoicesGrid>
        </>
      )}

      {totalPages > 1 && (
        <Pagination>
          <PageButton onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>Previous</span>
          </PageButton>
          <div tw="text-xs font-bold text-text-secondary uppercase tracking-widest">
            {page + 1} / {totalPages}
          </div>
          <PageButton
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            <span>Next</span>
            <FontAwesomeIcon icon={faChevronRight} />
          </PageButton>
        </Pagination>
      )}
    </Container>
  );
}
